import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const SplashClassic = () => {
  const navigate = useNavigate();
  const [stage, setStage] = useState(0);

  useEffect(() => {
    const timers = [
      setTimeout(() => setStage(1), 800),   // Fade in
      setTimeout(() => setStage(2), 3500),  // Stars appear
      setTimeout(() => setStage(3), 5000),  // Name reveals
      setTimeout(() => navigate('/onboarding-new'), 8000)
    ];

    return () => timers.forEach(timer => clearTimeout(timer));
  }, [navigate]);

  return (
    <div 
      className="min-h-screen flex items-center justify-center overflow-hidden relative"
      style={{ 
        background: 'linear-gradient(180deg, #0A0A0F 0%, #1A1A24 50%, #0A0A0F 100%)'
      }}
    >
      {/* Subtle vignette - very refined */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,0.7) 100%)'
        }}
      />

      <div className="relative z-10">
        <AnimatePresence mode="wait">
          {/* Stage 1: Initial fade in - elegant entrance */}
          {stage >= 1 && (
            <motion.div
              className="flex flex-col items-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 2, ease: 'easeInOut' }}
            >
              {/* Stage 2: Constellation appears - like Rolex crown reveal */}
              {stage >= 2 && (
                <motion.div
                  className="mb-16"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1.8, delay: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
                >
                  <svg width="320" height="120" viewBox="0 0 320 120">
                    <defs>
                      {/* EXACT logo gradient - cyan to purple */}
                      <linearGradient id="logoGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#22D3EE" />
                        <stop offset="30%" stopColor="#60A5FA" />
                        <stop offset="70%" stopColor="#A78BFA" />
                        <stop offset="100%" stopColor="#8B5CF6" />
                      </linearGradient>
                      
                      {/* Refined glow matching logo style */}
                      <filter id="refinedGlow" x="-50%" y="-50%" width="200%" height="200%">
                        <feGaussianBlur stdDeviation="6" result="blur"/>
                        <feComposite in="blur" in2="SourceGraphic" operator="over"/>
                      </filter>
                    </defs>
                    
                    {/* Connecting arc - EXACT logo style */}
                    <motion.path
                      d="M 60 95 Q 160 35, 260 95"
                      stroke="url(#logoGrad)"
                      strokeWidth="1.5"
                      fill="none"
                      opacity="0.5"
                      initial={{ pathLength: 0, opacity: 0 }}
                      animate={{ pathLength: 1, opacity: 0.5 }}
                      transition={{ duration: 2, delay: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
                    />
                    
                    {/* 5 Four-pointed stars - geometric, Art Deco style */}
                    {[
                      { x: 60, y: 95, size: 12, delay: 1.2 },
                      { x: 110, y: 63, size: 14, delay: 1.4 },
                      { x: 160, y: 40, size: 18, delay: 1.6 },
                      { x: 210, y: 63, size: 14, delay: 1.8 },
                      { x: 260, y: 95, size: 12, delay: 2.0 },
                    ].map((star, i) => (
                      <motion.g
                        key={i}
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ 
                          duration: 1.2, 
                          delay: star.delay,
                          ease: [0.25, 0.1, 0.25, 1]
                        }}
                      >
                        {/* Center bright core */}
                        <circle 
                          cx={star.x} 
                          cy={star.y} 
                          r={star.size * 0.15} 
                          fill="#FFFFFF" 
                        />
                        
                        {/* 4-pointed star - EXACT logo style */}
                        <path
                          d={`
                            M ${star.x} ${star.y - star.size}
                            L ${star.x + star.size * 0.35} ${star.y - star.size * 0.35}
                            L ${star.x + star.size} ${star.y}
                            L ${star.x + star.size * 0.35} ${star.y + star.size * 0.35}
                            L ${star.x} ${star.y + star.size}
                            L ${star.x - star.size * 0.35} ${star.y + star.size * 0.35}
                            L ${star.x - star.size} ${star.y}
                            L ${star.x - star.size * 0.35} ${star.y - star.size * 0.35}
                            Z
                          `}
                          fill="url(#logoGrad)"
                          filter="url(#refinedGlow)"
                        />
                      </motion.g>
                    ))}
                  </svg>
                </motion.div>
              )}

              {/* Stage 3: "taraanaya" text - EXACT LOGO STYLE */}
              {stage >= 3 && (
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 2, delay: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
                >
                  {/* Cyan glow behind text - matching logo */}
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      fontFamily: "'Brush Script MT', 'Lucida Handwriting', cursive",
                      fontSize: '96px',
                      fontWeight: 400,
                      color: '#22D3EE',
                      letterSpacing: '4px',
                      fontStyle: 'italic',
                      filter: 'blur(30px)',
                      opacity: 0.6,
                    }}
                  >
                    taraanaya
                  </div>
                  
                  {/* Main text - EXACT logo gradient cyan to purple */}
                  <h1
                    style={{
                      fontFamily: "'Brush Script MT', 'Lucida Handwriting', cursive",
                      fontSize: '96px',
                      fontWeight: 400,
                      background: 'linear-gradient(90deg, #22D3EE 0%, #60A5FA 30%, #A78BFA 70%, #8B5CF6 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      letterSpacing: '4px',
                      textAlign: 'center',
                      fontStyle: 'italic',
                      filter: 'drop-shadow(0 0 50px rgba(34, 211, 238, 0.5)) drop-shadow(0 0 80px rgba(139, 92, 246, 0.3))',
                      position: 'relative',
                    }}
                  >
                    taraanaya
                  </h1>

                  {/* Refined underline - matching logo gradient */}
                  <motion.div
                    className="mx-auto mt-8"
                    style={{
                      width: 200,
                      height: 1,
                      background: 'linear-gradient(90deg, transparent, #22D3EE 30%, #8B5CF6 70%, transparent)',
                    }}
                    initial={{ width: 0, opacity: 0 }}
                    animate={{ width: 200, opacity: 0.5 }}
                    transition={{ duration: 1.5, delay: 1.2, ease: [0.25, 0.1, 0.25, 1] }}
                  />
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default SplashClassic;
